// eslint-disable-next-line @typescript-eslint/triple-slash-reference -- Include ambient CSS types in downstream source programs.
/// <reference path="./styles.d.ts" />
/* eslint-disable react-refresh/only-export-components */
import {
  useId,
  useLayoutEffect,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from "react";

import { arkThemeStyleText, createArkTokens } from "./ArkThemeTokens";
import {
  readColorModePreference,
  readSystemDark,
  resolveColorMode,
  setColorModePreference,
  subscribeColorModePreference,
  subscribeSystemMode,
  type ColorModePreference,
} from "./colorModePreference";
import { Popover, PopoverContent, PopoverTrigger } from "./ShadcnPopover";
import { VisualEffectsProvider } from "./VisualEffects";
import { Label, FieldGroup, Radio } from "./controls";
import "./ArkTheme.css";
import "./Motion.css";

export type { ColorMode, ColorModePreference } from "./colorModePreference";

export function useColorMode() {
  const preference = useSyncExternalStore(
    subscribeColorModePreference,
    readColorModePreference,
    (): ColorModePreference => "system",
  );
  const systemDark = useSyncExternalStore(
    subscribeSystemMode,
    readSystemDark,
    () => false,
  );
  const mode = resolveColorMode(preference, systemDark);

  useLayoutEffect(() => {
    const root = document.documentElement;
    root.dataset.colorMode = mode;
    root.style.colorScheme = mode;
    root.classList.toggle("univer-dark", mode === "dark");
    let meta = document.querySelector<HTMLMetaElement>(
      'meta[name="theme-color"]',
    );
    if (!meta) {
      meta = document.createElement("meta");
      meta.name = "theme-color";
      document.head.append(meta);
    }
    meta.content = createArkTokens(mode).canvas;
  }, [mode]);

  return { mode, preference, setPreference: setColorModePreference } as const;
}

export function useArkTheme() {
  return createArkTokens(useColorMode().mode);
}

export function ArkThemeProvider({ children }: { children: ReactNode }) {
  useColorMode();
  return (
    <>
      <style data-ark-theme-tokens="">{arkThemeStyleText}</style>
      <VisualEffectsProvider>{children}</VisualEffectsProvider>
    </>
  );
}

function ColorModeSymbol({ preference }: { preference: ColorModePreference }) {
  return (
    <svg
      aria-hidden="true"
      fill="none"
      height="1em"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.7"
      viewBox="0 0 24 24"
      width="1em"
    >
      {preference === "system" ? (
        <>
          <rect height="13" rx="2" width="20" x="2" y="3" />
          <path d="M8 21h8M12 16v5" />
        </>
      ) : preference === "dark" ? (
        <path d="M20.9 13a9 9 0 0 1-9.9-9.9A9 9 0 1 0 20.9 13Z" />
      ) : (
        <>
          <circle cx="12" cy="12" r="4" />
          <path d="M12 2v2m0 16v2M2 12h2m16 0h2M4.9 4.9l1.4 1.4m11.4 11.4 1.4 1.4M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
        </>
      )}
    </svg>
  );
}

const options: ReadonlyArray<{
  value: ColorModePreference;
  label: string;
}> = [
  { value: "system", label: "跟随系统" },
  { value: "light", label: "浅色模式" },
  { value: "dark", label: "深色模式" },
];

export function ColorModeButton() {
  const { mode, preference, setPreference } = useColorMode();
  const [open, setOpen] = useState(false);
  const id = useId();
  return (
    <Popover onOpenChange={setOpen} open={open}>
      <PopoverTrigger
        aria-label="切换外观模式"
        className="ark-color-mode-trigger"
        title="外观"
        type="button"
      >
        <ColorModeSymbol preference={mode} />
      </PopoverTrigger>
      <PopoverContent align="end" aria-label="外观模式" side="bottom">
        <FieldGroup className="ark-color-mode-options" legend="外观模式">
          {options.map((option) => (
            <Label className="ark-color-mode-option" key={option.value}>
              <ColorModeSymbol preference={option.value} />
              <span>{option.label}</span>
              <Radio
                checked={preference === option.value}
                name={`color-mode-${id}`}
                onChange={() => setPreference(option.value)}
                size="small"
                value={option.value}
              />
            </Label>
          ))}
        </FieldGroup>
      </PopoverContent>
    </Popover>
  );
}
